import { getIntlLocale } from './chatUiLocale'

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/**
 * Display dates in dd/mm/yyyy in the chat UI.
 * Accepts ISO dates, common slash forms, and passes through unparseable text unchanged.
 */
export function formatChatDateDisplay(raw: string | undefined | null): string {
  if (raw == null) return ''
  const s = raw.trim()
  if (!s) return ''

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const [, y, mo, d] = iso
    return `${d}/${mo}/${y}`
  }

  const slash = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/)
  if (slash) {
    const a = parseInt(slash[1], 10)
    const b = parseInt(slash[2], 10)
    const y = slash[3]
    let dd: number
    let mm: number
    if (a > 12) {
      dd = a
      mm = b
    } else if (b > 12) {
      mm = a
      dd = b
    } else {
      dd = a
      mm = b
    }
    if (dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12) {
      return `${pad2(dd)}/${pad2(mm)}/${y}`
    }
  }

  const t = Date.parse(s)
  if (!Number.isNaN(t)) {
    const d = new Date(t)
    if (!Number.isNaN(d.getTime())) {
      return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
    }
  }

  return s
}

/**
 * Footer time for chat bubbles (WhatsApp-style): same calendar day shows time only;
 * otherwise a short local date + time.
 */
export function formatMessageListFooterTime(ts: number | undefined | null): string {
  if (ts == null || !Number.isFinite(ts)) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const loc = getIntlLocale()
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) {
    return d.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit' })
  }
  return d.toLocaleString(loc, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatProcessingMsLabel(processingMs: number): string {
  return formatLatencySeconds(processingMs)
}

/** Latency label always in seconds, e.g. ``1.2s`` or ``<0.1s``. */
export function formatLatencySeconds(processingMs: number | undefined | null): string {
  if (processingMs == null || !Number.isFinite(processingMs) || processingMs < 0) {
    return ''
  }
  const sec = processingMs / 1000
  if (sec < 0.05) return '<0.1s'
  return `${sec.toFixed(1)}s`
}

/** Gap since the previous chat bubble, e.g. ``+2.3s`` (hidden when under 100ms). */
export function formatInterMessageGapSeconds(gapMs: number | undefined | null): string | null {
  if (gapMs == null || !Number.isFinite(gapMs) || gapMs < 100) {
    return null
  }
  const label = formatLatencySeconds(gapMs)
  return label ? `+${label}` : null
}

export type ChatFooterTimingLabels = {
  voiceToChat?: string
  intent?: string
  normalize?: string
  typedCommand?: string
}

/** Footer label with optional command latency in brackets, e.g. ``10:42 AM (1.2s)``. */
export function formatMessageListFooterTimeWithTranscribe(
  ts: number | undefined | null,
  transcribeProcessingMs?: number,
  typedCommandProcessingMs?: number,
  intentProcessingMs?: number,
): string {
  const time = formatMessageListFooterTime(ts)
  if (!time) return ''
  const processingMs = transcribeProcessingMs ?? typedCommandProcessingMs ?? intentProcessingMs
  if (processingMs == null || !Number.isFinite(processingMs) || processingMs < 0) {
    return time
  }
  return `${time} (${formatProcessingMsLabel(processingMs)})`
}

/** User footer: speech end or submit → command bubble displayed (latency in seconds). */
export function formatUserFooterTimeWithTranscribe(
  ts: number | undefined | null,
  transcribeProcessingMs?: number,
  typedNormalizeProcessingMs?: number,
  labels?: ChatFooterTimingLabels,
): string {
  const time = formatMessageListFooterTime(ts)
  if (!time) return ''
  const latencyMs = transcribeProcessingMs ?? typedNormalizeProcessingMs
  if (latencyMs == null || !Number.isFinite(latencyMs) || latencyMs < 0) {
    return time
  }
  const stage =
    transcribeProcessingMs != null
      ? labels?.voiceToChat ?? 'voice→chat'
      : labels?.normalize ?? 'normalize'
  const latency = formatLatencySeconds(latencyMs)
  return latency ? `${time} · ${stage} ${latency}` : time
}

/** Assistant footer: latency is command display → response display only (seconds). */
export function formatAssistantFooterTimeWithLatency(
  ts: number | undefined | null,
  latencyMs?: number,
  labels?: ChatFooterTimingLabels,
): string {
  const time = formatMessageListFooterTime(ts)
  if (!time) return ''
  if (latencyMs == null || !Number.isFinite(latencyMs) || latencyMs < 0) {
    return time
  }
  const stage = labels?.intent ?? 'intent'
  const latency = formatLatencySeconds(latencyMs)
  return latency ? `${time} · ${stage} ${latency}` : time
}

/** Typed command footer: submit → assistant response (seconds). */
export function formatTypedCommandFooterTime(
  ts: number | undefined | null,
  typedCommandProcessingMs?: number,
  labels?: ChatFooterTimingLabels,
): string {
  const time = formatMessageListFooterTime(ts)
  if (!time) return ''
  if (
    typedCommandProcessingMs == null ||
    !Number.isFinite(typedCommandProcessingMs) ||
    typedCommandProcessingMs < 0
  ) {
    return time
  }
  const stage = labels?.typedCommand ?? 'command'
  const latency = formatLatencySeconds(typedCommandProcessingMs)
  return latency ? `${time} · ${stage} ${latency}` : time
}
